import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'tai_cart_v1';

// LocalStorage-backed shopping cart for Parts. Persists across page reloads
// and tabs. Each line is keyed by part_id + label (so the same part with
// different pricing tiers stays as separate lines).
export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* noop */ }
  }, [items]);

  const lineKey = (i) => `${i.part_id}|${i.label || ''}`;

  const add = useCallback((item) => {
    setItems(prev => {
      const k = lineKey(item);
      const existing = prev.find(p => lineKey(p) === k);
      if (existing) {
        return prev.map(p => lineKey(p) === k ? { ...p, qty: (p.qty || 1) + (item.qty || 1) } : p);
      }
      return [...prev, { ...item, qty: item.qty || 1 }];
    });
  }, []);

  const remove = useCallback((part_id, label = '') => {
    setItems(prev => prev.filter(p => !(p.part_id === part_id && (p.label || '') === label)));
  }, []);

  const setQty = useCallback((part_id, label, qty) => {
    const n = Math.max(1, Number(qty) || 1);
    setItems(prev => prev.map(p => (p.part_id === part_id && (p.label || '') === (label || '')) ? { ...p, qty: n } : p));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const count = items.reduce((s, i) => s + (i.qty || 1), 0);
    const subtotal = items.reduce((s, i) => {
      // pricing tier lines use unit_price as the tier's bundled price
      if (i.label) return s + Number(i.unit_price || 0) * (i.qty || 1);
      return s + Number(i.unit_price || 0) * (i.qty || 1);
    }, 0);
    return { count, subtotal };
  }, [items]);

  return (
    <CartContext.Provider value={{ items, add, remove, setQty, clear, ...totals }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
